import type { Contact } from './types.js';

/** Lower-case, accent-stripped text for matching (search and lookups). */
export function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * The best existing contact for a typed name / organization (§6.1 lookup, §19.3 captures).
 * Deliberately strict: a prefix of the person or company name (3+ chars) or of a word in
 * them (4+ chars). A wrong match would turn a new prospect into an update, so substring and
 * subsequence matches are left to the Table search.
 */
export function matchProspect(contacts: Contact[], name: string, organization: string): Contact | null {
  const score = (q: string, text: string): number => {
    const t = normalize(text);
    if (!t || q.length < 3) return 0;
    if (t.startsWith(q)) return 100;
    if (q.length >= 4 && t.split(/\s+/).some((w) => w.startsWith(q))) return 80;
    return 0;
  };
  const q1 = normalize(name.trim());
  const q2 = normalize(organization.trim());
  let best: { c: Contact; s: number } | null = null;
  for (const c of contacts) {
    if (c.archived) continue;
    const s = Math.max(score(q1, c.contact), score(q1, c.companyName), score(q2, c.companyName));
    // Ties go to the shorter name, so "Nimbus Software" beats "Nimbus Software 57".
    if (s > 0 && (!best || s > best.s || (s === best.s && c.companyName.length < best.c.companyName.length))) best = { c, s };
  }
  return best?.c ?? null;
}
